import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, ArrowLeft, ChevronRight, CheckCircle, XCircle, AlertCircle, Clock,
  FileText, User, Building2, DollarSign, MessageSquare, Send, Info, Shield
} from "lucide-react";
import { API } from "../data/constants";

const STAGE_META = {
  submitted:    { label: "Submitted",    color: "bg-blue-500",    text: "text-blue-700",  bg: "bg-blue-50" },
  in_review:    { label: "In Review",    color: "bg-amber-500",   text: "text-amber-700", bg: "bg-amber-50" },
  needs_info:   { label: "Needs Info",   color: "bg-orange-500",  text: "text-orange-700",bg: "bg-orange-50" },
  underwriting: { label: "Underwriting", color: "bg-purple-500",  text: "text-purple-700",bg: "bg-purple-50" },
  approved:     { label: "Approved",     color: "bg-emerald-500", text: "text-emerald-700",bg: "bg-emerald-50" },
  declined:     { label: "Declined",     color: "bg-red-500",     text: "text-red-700",   bg: "bg-red-50" },
};
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up" };
const NOTE_TYPES = [
  { value: "decision", label: "Decision" },
  { value: "observation", label: "Observation" },
  { value: "condition", label: "Condition" },
  { value: "general", label: "General" },
];
const INFO_CATEGORIES = [
  { value: "document", label: "Document" },
  { value: "clarification", label: "Clarification" },
  { value: "financial", label: "Financial" },
  { value: "general", label: "General" },
];

// Valid transitions from schemas.py
const VALID_TRANSITIONS = {
  submitted: ["in_review", "declined"],
  in_review: ["underwriting", "needs_info", "declined"],
  needs_info: ["in_review", "declined"],
  underwriting: ["approved", "needs_info", "declined"],
  approved: ["capital_matching"],
};

import { fmtUSD } from "../utils/currency";
function fmt$(v) { return fmtUSD(v); }

export default function UnderwritingDetail({ leadId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [transitioning, setTransitioning] = useState(false);
  const [transitionReason, setTransitionReason] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [noteType, setNoteType] = useState("decision");
  const [submittingNote, setSubmittingNote] = useState(false);
  const [infoItems, setInfoItems] = useState([{ label: "", category: "document" }]);
  const [infoMessage, setInfoMessage] = useState("");
  const [submittingInfo, setSubmittingInfo] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDetail = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/underwriting/deal/${leadId}`, { headers })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const handleTransition = async (toStage) => {
    setTransitioning(true);
    try {
      await axios.post(`${API}/underwriting/deal/${leadId}/transition`,
        { to_stage: toStage, reason: transitionReason || null }, { headers });
      setTransitionReason("");
      fetchDetail();
    } catch (err) {
      alert(err.response?.data?.detail || "Transition failed");
    } finally { setTransitioning(false); }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) return;
    setSubmittingNote(true);
    try {
      await axios.post(`${API}/underwriting/deal/${leadId}/notes`,
        { content: noteContent, note_type: noteType, visibility: "internal" }, { headers });
      setNoteContent("");
      fetchDetail();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to add note");
    } finally { setSubmittingNote(false); }
  };

  const handleRequestInfo = async () => {
    const validItems = infoItems.filter(it => it.label.trim());
    if (!validItems.length) return;
    setSubmittingInfo(true);
    try {
      await axios.post(`${API}/underwriting/deal/${leadId}/request-info`,
        { items: validItems, message: infoMessage || null }, { headers });
      setInfoItems([{ label: "", category: "document" }]);
      setInfoMessage("");
      fetchDetail();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to request info");
    } finally { setSubmittingInfo(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-purple-600" /></div>;
  if (!data) return <div className="text-center py-20 text-sm text-[#888]">Deal not found</div>;

  const { deal, extra, borrower, documents, notes, info_requests, completeness, decision_readiness } = data;
  const stage = deal.stage;
  const stageMeta = STAGE_META[stage] || STAGE_META.submitted;
  const transitions = VALID_TRANSITIONS[stage] || [];

  const tabs = [
    { key: "overview", label: "Overview" },
    { key: "documents", label: `Documents (${documents.length})` },
    { key: "notes", label: `Notes (${notes.length})` },
    { key: "info_requests", label: `Info Requests (${info_requests.length})` },
  ];

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-8" data-testid="underwriting-detail">
      {/* Back + Header */}
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-[#888] hover:text-[#333] mb-4 transition-colors" data-testid="uw-back-btn">
        <ArrowLeft className="h-4 w-4" /> Back to Pipeline
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-bold text-[#0A0A0A]">{deal.deal_number}</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${stageMeta.bg} ${stageMeta.text}`} data-testid="deal-stage-badge">
              {stageMeta.label}
            </span>
          </div>
          <p className="text-sm text-[#666]">
            {deal.borrower_name} · {LOAN_LABELS[deal.loan_type] || deal.loan_type} · {deal.property_address || deal.state}
          </p>
        </div>

        {/* Stage Transitions */}
        {transitions.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="transition-actions">
            <input
              type="text"
              placeholder="Reason (optional)"
              value={transitionReason}
              onChange={e => setTransitionReason(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#e5e5e5] text-sm w-48 focus:outline-none focus:ring-2 focus:ring-purple-200"
              data-testid="transition-reason"
            />
            {transitions.map(t => {
              const isDecline = t === "declined";
              const isApprove = t === "approved";
              return (
                <button
                  key={t}
                  onClick={() => handleTransition(t)}
                  disabled={transitioning}
                  data-testid={`transition-${t}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    isDecline ? "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                    : isApprove ? "bg-emerald-600 text-white hover:bg-emerald-700"
                    : "bg-purple-50 text-purple-700 border border-purple-200 hover:bg-purple-100"
                  } disabled:opacity-50`}
                >
                  {transitioning ? <Loader2 className="h-4 w-4 animate-spin" /> : (STAGE_META[t]?.label || t)}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Decision Readiness Panel */}
      <div className={`rounded-xl border p-4 mb-6 ${decision_readiness.ready ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="decision-readiness-panel">
        <div className="flex items-center gap-2 mb-3">
          <Shield className={`h-4 w-4 ${decision_readiness.ready ? "text-emerald-600" : "text-amber-600"}`} />
          <span className="text-sm font-semibold text-[#0A0A0A]">
            Decision Readiness: {decision_readiness.ready ? "Ready" : "Not Ready"}
          </span>
          <span className="text-xs text-[#888] ml-auto">Completeness {completeness.pct}%</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {Object.entries(decision_readiness).filter(([k]) => k !== "ready").map(([key, val]) => (
            <div key={key} className="flex items-center gap-1.5 text-xs">
              {val ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> : <XCircle className="h-3.5 w-3.5 text-red-400" />}
              <span className={val ? "text-[#444]" : "text-red-600 font-medium"}>{key.replace(/_/g, " ")}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#eee] mb-6">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`uw-tab-${t.key}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-purple-600 text-purple-700" : "border-transparent text-[#888] hover:text-[#333]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
          {tab === "overview" && <OverviewTab deal={deal} extra={extra} borrower={borrower} completeness={completeness} />}
          {tab === "documents" && <DocumentsTab documents={documents} />}
          {tab === "notes" && (
            <NotesTab notes={notes} noteContent={noteContent} setNoteContent={setNoteContent}
              noteType={noteType} setNoteType={setNoteType} onSubmit={handleAddNote} submitting={submittingNote} />
          )}
          {tab === "info_requests" && (
            <InfoRequestsTab requests={info_requests} items={infoItems} setItems={setInfoItems}
              message={infoMessage} setMessage={setInfoMessage} onSubmit={handleRequestInfo} submitting={submittingInfo} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}


/* ── Overview Tab ─────────────────────────────────────────── */
function OverviewTab({ deal, extra, borrower, completeness }) {
  const sections = [
    {
      title: "Deal Summary", icon: DollarSign, items: [
        ["Loan Type", LOAN_LABELS[deal.loan_type] || deal.loan_type],
        ["Purchase Price", fmt$(deal.purchase_price)],
        ["Rehab Budget", fmt$(deal.rehab_budget)],
        ["ARV", fmt$(deal.arv)],
        ["Requested Loan", fmt$(deal.requested_loan_amount)],
        ["LTV", deal.purchase_price ? `${((deal.requested_loan_amount / deal.purchase_price) * 100).toFixed(1)}%` : "—"],
        ["LTARV", deal.arv ? `${((deal.requested_loan_amount / deal.arv) * 100).toFixed(1)}%` : "—"],
        ["Exit Strategy", extra?.exit_strategy || "—"],
      ],
    },
    {
      title: "Property", icon: Building2, items: [
        ["Address", deal.property_address || "—"],
        ["Type", extra?.property_type || deal.property_type || "—"],
        ["Beds/Baths", `${extra?.bedrooms || "—"} / ${extra?.bathrooms || "—"}`],
        ["Sqft", extra?.sqft ? `${Number(extra.sqft).toLocaleString()} sqft` : "—"],
        ["Year Built", extra?.year_built || "—"],
      ],
    },
    {
      title: "Borrower", icon: User, items: [
        ["Name", borrower?.full_name || deal.borrower_name || "—"],
        ["Email", borrower?.email || extra?.email || "—"],
        ["Phone", borrower?.phone || extra?.phone || "—"],
        ["Entity", borrower?.entity_name || extra?.entity_name || "—"],
        ["Experience", borrower?.experience != null ? `${borrower.experience} projects` : extra?.experience_projects_completed != null ? `${extra.experience_projects_completed} projects` : "—"],
        ["FICO", deal.fico_estimate || extra?.fico_estimate || "—"],
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Completeness Bar */}
      <div className="rounded-xl border border-[#eee] bg-white p-4" data-testid="completeness-bar">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-[#333]">Completeness</span>
          <span className="text-sm font-bold text-[#0A0A0A]">{completeness.pct}%</span>
        </div>
        <div className="w-full h-2 rounded-full bg-[#f0f0f0]">
          <div className={`h-2 rounded-full transition-all duration-500 ${completeness.pct >= 80 ? "bg-emerald-500" : "bg-amber-500"}`}
            style={{ width: `${completeness.pct}%` }} />
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {Object.entries(completeness.checks).map(([k, v]) => (
            <span key={k} className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${v ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-500"}`}>
              {v ? "✓" : "✗"} {k.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      </div>

      {/* Info sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {sections.map(s => (
          <div key={s.title} className="rounded-xl border border-[#eee] bg-white p-4">
            <div className="flex items-center gap-2 mb-3">
              <s.icon className="h-4 w-4 text-purple-500" />
              <span className="text-sm font-semibold text-[#0A0A0A]">{s.title}</span>
            </div>
            <dl className="space-y-2">
              {s.items.map(([label, value]) => (
                <div key={label} className="flex justify-between text-xs">
                  <dt className="text-[#888]">{label}</dt>
                  <dd className="text-[#333] font-medium text-right max-w-[60%] truncate">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ── Documents Tab ────────────────────────────────────────── */
function DocumentsTab({ documents }) {
  if (!documents.length) return (
    <div className="text-center py-12">
      <FileText className="h-8 w-8 text-[#ccc] mx-auto mb-2" />
      <p className="text-sm text-[#888]">No documents uploaded</p>
    </div>
  );
  return (
    <div className="space-y-2" data-testid="uw-documents-list">
      {documents.map((doc, i) => (
        <div key={doc.doc_id || i} className="flex items-center gap-3 rounded-xl border border-[#eee] bg-white p-3">
          <FileText className="h-5 w-5 text-purple-400 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm text-[#0A0A0A] truncate">{doc.filename}</div>
            <div className="text-xs text-[#888]">{doc.category_label || doc.category} · {doc.size ? `${(doc.size / 1024).toFixed(0)} KB` : ""}</div>
          </div>
          <span className="text-[10px] text-[#aaa]">{doc.created_at?.slice(0, 10)}</span>
        </div>
      ))}
    </div>
  );
}


/* ── Notes Tab ────────────────────────────────────────────── */
function NotesTab({ notes, noteContent, setNoteContent, noteType, setNoteType, onSubmit, submitting }) {
  return (
    <div className="space-y-4">
      {/* Add note form */}
      <div className="rounded-xl border border-[#eee] bg-white p-4" data-testid="add-note-form">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="h-4 w-4 text-purple-500" />
          <span className="text-sm font-semibold text-[#0A0A0A]">Add Note</span>
        </div>
        <div className="flex gap-2 mb-3">
          {NOTE_TYPES.map(t => (
            <button key={t.value} onClick={() => setNoteType(t.value)} data-testid={`note-type-${t.value}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                noteType === t.value ? "bg-purple-100 text-purple-700 border border-purple-200" : "bg-[#f8f8f8] text-[#888] border border-transparent hover:border-[#ddd]"
              }`}>
              {t.label}
            </button>
          ))}
        </div>
        <textarea
          value={noteContent}
          onChange={e => setNoteContent(e.target.value)}
          placeholder="Write your underwriting note..."
          data-testid="note-content-input"
          className="w-full p-3 rounded-lg border border-[#e5e5e5] text-sm resize-none h-24 focus:outline-none focus:ring-2 focus:ring-purple-200"
        />
        <button onClick={onSubmit} disabled={submitting || !noteContent.trim()} data-testid="submit-note-btn"
          className="mt-2 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 transition-all flex items-center gap-2">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Add Note
        </button>
      </div>

      {/* Notes list */}
      <div className="space-y-2">
        {notes.map((n, i) => (
          <div key={n.note_id || i} className="rounded-xl border border-[#eee] bg-white p-4" data-testid={`note-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-[#444]">{n.author_name}</span>
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                n.note_type === "decision" ? "bg-purple-50 text-purple-600" :
                n.note_type === "condition" ? "bg-amber-50 text-amber-600" :
                n.note_type === "observation" ? "bg-blue-50 text-blue-600" :
                "bg-[#f5f5f5] text-[#888]"
              }`}>{n.note_type}</span>
              <span className="text-[10px] text-[#aaa] ml-auto">{n.created_at?.slice(0, 16).replace("T", " ")}</span>
            </div>
            <p className="text-sm text-[#333] whitespace-pre-wrap">{n.content}</p>
          </div>
        ))}
        {!notes.length && <p className="text-sm text-[#888] text-center py-6">No notes yet</p>}
      </div>
    </div>
  );
}


/* ── Info Requests Tab ────────────────────────────────────── */
function InfoRequestsTab({ requests, items, setItems, message, setMessage, onSubmit, submitting }) {
  const addItem = () => setItems([...items, { label: "", category: "document" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => setItems(items.map((it, idx) => idx === i ? { ...it, [field]: val } : it));

  return (
    <div className="space-y-4">
      {/* Create new request */}
      <div className="rounded-xl border border-[#eee] bg-white p-4" data-testid="create-info-request-form">
        <div className="flex items-center gap-2 mb-3">
          <Info className="h-4 w-4 text-orange-500" />
          <span className="text-sm font-semibold text-[#0A0A0A]">Request Information</span>
        </div>
        <div className="space-y-2 mb-3">
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <select value={item.category} onChange={e => updateItem(i, "category", e.target.value)}
                className="px-2 py-2 rounded-lg border border-[#e5e5e5] text-xs bg-white" data-testid={`info-category-${i}`}>
                {INFO_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <input
                type="text" value={item.label} onChange={e => updateItem(i, "label", e.target.value)}
                placeholder="What do you need?"
                data-testid={`info-item-${i}`}
                className="flex-1 px-3 py-2 rounded-lg border border-[#e5e5e5] text-sm focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
              {items.length > 1 && (
                <button onClick={() => removeItem(i)} className="text-[#ccc] hover:text-red-400 text-sm">✗</button>
              )}
            </div>
          ))}
        </div>
        <button onClick={addItem} className="text-xs text-purple-600 hover:text-purple-800 mb-3" data-testid="add-info-item-btn">+ Add item</button>
        <textarea
          value={message} onChange={e => setMessage(e.target.value)}
          placeholder="Optional message to borrower..."
          data-testid="info-message-input"
          className="w-full p-3 rounded-lg border border-[#e5e5e5] text-sm resize-none h-16 focus:outline-none focus:ring-2 focus:ring-orange-200"
        />
        <button onClick={onSubmit} disabled={submitting || !items.some(it => it.label.trim())} data-testid="submit-info-request-btn"
          className="mt-2 px-4 py-2 rounded-lg bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition-all flex items-center gap-2">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send Request
        </button>
      </div>

      {/* Existing requests */}
      <div className="space-y-3">
        {requests.map((req, i) => (
          <div key={req.request_id || i} className={`rounded-xl border p-4 ${req.status === "open" ? "border-orange-200 bg-orange-50/50" : "border-[#eee] bg-white"}`}
            data-testid={`info-request-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${req.status === "open" ? "bg-orange-100 text-orange-600" : "bg-emerald-100 text-emerald-600"}`}>
                {req.status}
              </span>
              <span className="text-xs text-[#888]">by {req.requested_by_name}</span>
              <span className="text-[10px] text-[#aaa] ml-auto">{req.created_at?.slice(0, 10)}</span>
            </div>
            {req.message && <p className="text-sm text-[#555] mb-2">{req.message}</p>}
            <div className="space-y-1">
              {req.items?.map((it, j) => (
                <div key={j} className="flex items-center gap-2 text-xs">
                  {it.fulfilled ? <CheckCircle className="h-3.5 w-3.5 text-emerald-500" /> : <Clock className="h-3.5 w-3.5 text-orange-400" />}
                  <span className={`px-1.5 py-0.5 rounded bg-[#f5f5f5] text-[10px] text-[#888]`}>{it.category}</span>
                  <span className={it.fulfilled ? "text-[#888] line-through" : "text-[#333]"}>{it.label}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        {!requests.length && <p className="text-sm text-[#888] text-center py-6">No info requests</p>}
      </div>
    </div>
  );
}
